import api from '../services/authService';

export const claimNextItemAPI = async () => {
  try {
    const response = await api.post('/mail-intake/claim-next/');
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || "No documents available";
    throw { message: errorMessage, status: error.response?.status || 500 };
  }
};

// Claim a specific intake item by id. Backed by the new endpoint
// POST /mail-intake/<id>/claim/. Used by the List tab's row-click flow
// so a sorter can jump directly to a chosen document instead of being
// limited to claim-next's FIFO order.
export const claimSpecificItemAPI = async (itemId) => {
  try {
    const response = await api.post(`/mail-intake/${itemId}/claim/`);
    return response;
  } catch (error) {
    // Propagate the backend's specific 409 messages so the UI can show
    // "Currently being processed by Alex Rivera" instead of a generic
    // "Could not open this document".
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      "Could not open this document";
    throw { message: errorMessage, status: error.response?.status || 500 };
  }
};

export const assignItemAPI = async ({ itemId, matterId, documentName }) => {
  try {
    const payload = { matter_id: matterId };
    if (documentName) payload.document_name = documentName;
    const response = await api.post(`/mail-intake/${itemId}/assign/`, payload);
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to assign item";
    throw { message: errorMessage, status: error.response?.status || 500 };
  }
};

export const skipItemAPI = async (itemId) => {
  try {
    const response = await api.post(`/mail-intake/${itemId}/skip/`);
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to skip item";
    throw { message: errorMessage, status: error.response?.status || 500 };
  }
};

export const deleteItemAPI = async (itemId) => {
  try {
    const response = await api.post(`/mail-intake/${itemId}/delete/`);
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to delete item";
    throw { message: errorMessage, status: error.response?.status || 500 };
  }
};

export const fetchQueueListAPI = async ({
  page,
  page_size,
  status,
  search = "",
}) => {
  try {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('page_size', page_size.toString());
    // "ACTIVE" and the legacy "ALL" are both client-side sentinels meaning
    // "don't send a status filter" -- the backend's default scope already
    // returns the active queue (uploading + pending + claimed + skipped).
    // Any other value (pending / claimed / skipped / assigned / deleted)
    // is passed through verbatim so terminal-state rows stay reachable.
    if (status && status !== "ACTIVE" && status !== "ALL") {
      params.set('status', status);
    }
    if (search) params.set('search', search);
    const response = await api.get(`/mail-intake/queue/?${params.toString()}`);
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to fetch queue";
    throw { message: errorMessage, status: error.response?.status || 500 };
  }
};

export const searchMattersAPI = async (query) => {
  try {
    // The dedicated Assign-screen search: relevance-ranked, no page
    // rollups. The old Matters-page endpoint took up to ~50s under
    // fuzzy multi-word terms and left the box stuck on "Searching…"
    // (Matthew, 2026-07-18). The timeout is a hard stop so a slow
    // backend can never hang the picker again.
    const response = await api.get(
      `/mail-intake/matter-search/?q=${encodeURIComponent(query)}`,
      { timeout: 15000 }
    );
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to search matters";
    throw { message: errorMessage, status: error.response?.status || 500 };
  }
};

export const fetchMatterIdentityAPI = async (matterId) => {
  try {
    const response = await api.get(
      `/mail-intake/matter-identity/${encodeURIComponent(matterId)}/`,
      { timeout: 15000 }
    );
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to load client record";
    throw { message: errorMessage, status: error.response?.status || 500 };
  }
};

export const fetchIntakeStatsAPI = async ({ period = "today", date = "" }) => {
  try {
    const params = date
      ? `date=${encodeURIComponent(date)}`
      : `period=${encodeURIComponent(period)}`;
    const response = await api.get(`/mail-intake/stats/?${params}`);
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to fetch stats";
    throw { message: errorMessage, status: error.response?.status || 500 };
  }
};

export const fetchIntakeCountsAPI = async () => {
  try {
    const response = await api.get('/mail-intake/counts/');
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to fetch counts";
    throw { message: errorMessage, status: error.response?.status || 500 };
  }
};

export const uploadIntakeDocumentAPI = async ({ filename, file_size, content_type, source }) => {
  try {
    const response = await api.post('/mail-intake/upload/', {
      filename, file_size, content_type, source,
    });
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to get upload URL";
    throw { message: errorMessage, status: error.response?.status || 500 };
  }
};

export const completeIntakeUploadAPI = async (intakeItemId) => {
  try {
    const response = await api.post('/mail-intake/upload/complete/', {
      intake_item_id: intakeItemId,
    });
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to complete upload";
    throw { message: errorMessage, status: error.response?.status || 500 };
  }
};

// Read-only refresh of a single intake item (full detail payload,
// including intake_pipeline_status and extracted_markdown). Backed by
// GET /intake/documents/<id>/ — used by the Assign view to poll while
// the AI is still extracting the currently-open document.
export const fetchIntakeItemAPI = async (itemId) => {
  const response = await api.get(`/intake/documents/${itemId}/`);
  return response;
};
